import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  BaseResourceItemsComponent,
  DateFormatPipe,
  FavorecidoCertificadoStorage,
  FavorecidoService,
  GlobalService
} from 'eddydata-lib';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { FavorecidoCertificadoStorageService } from '../service/favorecido-certificado-storage.service';

type ReferenciaDocumento = {
  valor: string;
  label: string;
  campoData?: string;
};

@Component({
  selector: 'lib-favorecido-certificado-storage',
  templateUrl: './favorecido-certificado-storage.component.html'
})
export class FavorecidoCertificadoStorageComponent extends BaseResourceItemsComponent<FavorecidoCertificadoStorage> implements OnChanges {

  @Input() public favorecidoId: number;
  @Input() public vencimentosForm: FormGroup;
  @Input() public habilitarEdicao: boolean = false;

  @Output() atualizarTela = new EventEmitter<boolean>();

  public ptBR: any;
  public carregando: boolean = false;

  public readonly referenciasDocumento: ReferenciaDocumento[] = [
    { valor: 'CERTIDAO', label: 'CERTIDÃO', campoData: 'data_certidao' },
    { valor: 'FGTS', label: 'F.G.T.S.', campoData: 'data_fgts' },
    { valor: 'INSS', label: 'I.N.S.S.', campoData: 'data_inss' },
    { valor: 'IRRF', label: 'I.R.R.F.', campoData: 'data_irrf' },
    { valor: 'ENTIDADE_PROFISSIONAL', label: 'ENTIDADE PROFISSIONAL', campoData: 'data_entidade_profissional' },
    { valor: 'ALVARA_SANITARIO', label: 'ALVARÁ SANITÁRIO', campoData: 'data_alvara_social' },
    { valor: 'CONTRATO_SOCIAL', label: 'CONTRATO SOCIAL', campoData: 'data_contrato_social' },
    { valor: 'BALANCO_ULTIMO_EXERCICIO', label: 'BALANÇO DO ULTIMO EXERCÍCIO', campoData: 'data_balanco_exercicio' },
    { valor: 'CERTIDAO_CONJUNTA_FEDERAL', label: 'CERTIDÃO CONJUNTA FEDERAL', campoData: 'data_conjunta_federal' },
    { valor: 'DIVIDA_ATIVA_MUNICIPAL', label: 'DÍVIDA ATIVA MUNICIPAL', campoData: 'data_divida_ativa_municipal' },
    { valor: 'ICMS', label: 'I.C.M.S.', campoData: 'data_icms' },
    { valor: 'CERTIDAO_NEGATIVA_DEBITOS_TRABALHISTAS', label: 'CERTIDÃO NEGATIVA DE DÉBITOS TRABALHISTAS', campoData: 'data_debitos_trabalhistas' },
    { valor: 'CERT_NEG_FALENCIA_CONCORDATA', label: 'CERT. NEG. DE FALÊNCIA E CONCORDATA', campoData: 'data_certidao_falencia' },
    { valor: 'CERTIDAO_NEGATIVA_ESTADUAL', label: 'CERTIDÃO NEGATIVA ESTADUAL', campoData: 'data_certidao_estadual' },
    { valor: 'CERTIDAO_NEGATIVA_MUNICIPAL', label: 'CERTIDÃO NEGATIVA MUNICIPAL', campoData: 'data_certidao_municipal' },
    { valor: 'OUTROS', label: 'OUTROS' }
  ];

  constructor(
    private globalService: GlobalService,
    private favorecidoService: FavorecidoService,
    public favorecidoCertificadoStorageService: FavorecidoCertificadoStorageService
  ) {
    super(new FavorecidoCertificadoStorage(), favorecidoCertificadoStorageService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['favorecidoId'] && !changes['favorecidoId'].firstChange) {
      this.carregar();
    }
  }

  protected afterInit(): void {
    this.ptBR = this.globalService.obterDataBR();
    this.carregar();
  }

  protected validSave(item: FavorecidoCertificadoStorage): boolean {
    if (!this.favorecidoId) {
      toastr.warning('Favorecido não identificado!');
      return false;
    }
    if (!item.referencia_documento) {
      toastr.warning('Informe a referência do documento!');
      return false;
    }
    if (!item.data_vencimento) {
      toastr.warning('Informe a data de vencimento!');
      return false;
    }
    if (!item.arquivo) {
      toastr.warning('Selecione o anexo!');
      return false;
    }
    return true;
  }

  protected beforeSave(item: FavorecidoCertificadoStorage): void {
    item.orgao = this.login.orgao;
  }

  public adicionar() {
    super.adicionar();
    const item = this.selecionarEditavel();
    if (item) {
      item.referencia_documento = null;
      item.data_vencimento = null;
      item.situacao = null;
      item.arquivo = null;
    }
  }

  public async salvar(item: FavorecidoCertificadoStorage): Promise<void | boolean> {
    if (!(await this.validSave(item)).valueOf()) {
      return false;
    }

    const existe = this.lista.find(d => d.referencia_documento == item.referencia_documento && this.getSituacao(d) == 'VIGENTE' && d.id);
    if (existe) {
      toastr.warning(`Já existe um item vigente para a referência informada!`);
      return
    }

    this.beforeSave(item);
    item.situacao = this.getSituacao(item);
    this.carregando = true;

    this.favorecidoCertificadoStorageService.upload(
      item.arquivo,
      this.login.orgao.id,
      this.favorecidoId,
      item,
      this.getReferenciaDescricao(item.referencia_documento)
    ).pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        toastr.success('Certificado salvo com sucesso!');
        this.carregar();
        this.atualizarTela.emit(true);
      }, error => {
        this.carregando = false;
        item['editavel'] = true;
        toastr.error(error?.error?.payload || 'Erro ao salvar certificado.');
      });
  }

  public carregar() {
    if (!this.favorecidoId || !this.login?.orgao?.id) {
      return;
    }

    this.carregando = true;
    this.favorecidoCertificadoStorageService.filtrar(1, -1, {
      'favorecido.id': this.favorecidoId,
      orgao_id: this.login.orgao.id,
      relations: 'responsavel',
      orderBy: 'data_cadastro$DESC'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.lista = res ? res.content : [];
        this.carregando = false;
        this.change();
      }, error => {
        this.carregando = false;
        toastr.error(error?.error?.payload || 'Erro ao carregar certificados.');
      });
  }

  public alterarReferencia(item: FavorecidoCertificadoStorage) {
    const referencia = this.referenciasDocumento.find((opcao) => opcao.valor === item.referencia_documento);
    const dataCampo = referencia?.campoData ? this.vencimentosForm?.get(referencia.campoData)?.value : null;

    item.data_vencimento = dataCampo ? new DateFormatPipe().transform(dataCampo, []) : null;
    this.atualizarSituacao(item);
  }

  public atualizarSituacao(item: FavorecidoCertificadoStorage) {
    item.situacao = this.getSituacao(item);
  }

  public selecionarArquivo(event: Event, item: FavorecidoCertificadoStorage) {
    const elemento = event.target as HTMLInputElement;
    const arquivo = elemento?.files && elemento.files.length > 0 ? elemento.files[0] : null;

    const referencia = this.referenciasDocumento.find((opcao) => opcao.valor === item.referencia_documento);
    const campoData = referencia?.campoData ? referencia.campoData : null;
    // const dataCampo = campoData ? this.vencimentosForm?.get(campoData)?.value : null;

    // if (campoData && !dataCampo) {
    //   toastr.warning(`Informe a data de vencimento para ${referencia?.label || item.referencia_documento}`);
    //   elemento.value = '';
    //   return;
    // }

    item.arquivo = arquivo;
    elemento.value = '';
  }

  public getSituacao(item: FavorecidoCertificadoStorage): string {
    return this.calcularSituacao(item?.data_vencimento);
  }

  public getSituacaoClass(item: FavorecidoCertificadoStorage): string {
    return this.getSituacao(item) === 'VENCIDO' ? 'badge badge-danger' : 'badge badge-success';
  }

  public getReferenciaDescricao(referenciaDocumento: string): string {
    return this.referenciasDocumento.find((item) => item.valor === referenciaDocumento)?.label || referenciaDocumento || '-';
  }

  private calcularSituacao(dataVencimento: Date | string): string {
    const data = this.converterData(dataVencimento);

    if (!data) {
      return null;
    }

    const vencimento = new Date(data.getFullYear(), data.getMonth(), data.getDate(), 0, 0, 0);
    const hoje = new Date();
    const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0);

    return vencimento < hojeSemHora ? 'VENCIDO' : 'VIGENTE';
  }

  private converterData(data: Date | string): Date {
    if (!data) {
      return null;
    }

    if (data instanceof Date) {
      return data;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data.split('-').map((item) => +item);
      return new Date(ano, mes - 1, dia, 0, 0, 0);
    }

    const dataConvertida = new Date(data);
    return isNaN(dataConvertida.getTime()) ? null : dataConvertida;
  }
}
